import cssMobile from '../../css/editorMobile.module.css'
import {useEffect, useState} from 'react'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {
  faAlignLeft,
  faArrowsAlt,
  faArrowsAltH,
  faArrowsAltV,
  faFont,
  faMagic,
  faPaintBrush,
  faPlus,
  faShare,
  faSortNumericUp,
  faTextWidth,
  faTrash,
  faUndo,
} from '@fortawesome/free-solid-svg-icons'
import {useAppDispatch, useAppSelector} from '../../stores/configureStore'
import {
  addImageHistoryItem,
  addImageSelected,
  deleteImage,
} from '../../stores/editor/editor.reducer'
import {Img} from '../../models/editor/img'
import {IMG_ADDED, IMG_REMOVED} from '../../models/editor/imgHistoryItemAction'
import ReorderControls from './controls/ReorderControls'
import TextControls from './controls/TextControls'
import FontControls from './controls/FontControls'
import ColorControls from './controls/ColorControls'
import StyleControls from './controls/StyleControls'
import SizeControlsCaption from './controls/SizeControlsCaption'
import RotationControls from './controls/RotationControls'
import CurveControls from './controls/CurveControls'
import Distance1Controls from './controls/Distance1Controls'
import Distance2Controls from './controls/Distance2Controls'
import MoveControls from './controls/MoveControls'
import MirrorControls from './controls/MirrorControls'
import {selectToolboxTabPanel} from '../../stores/toolbox/toolbox.reducer'
import {ImgCaption} from '../../models/editor/imgCaption'
import {v4 as uuidv4} from 'uuid'
import {origo} from '../../utils/helpers/origo'
import {useTranslation} from 'react-i18next'

interface CaptionProps {
  active: boolean
  isGraphics?: boolean
  setControlOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export default function Caption({
  active,
  isGraphics,
  setControlOpen,
}: CaptionProps) {
  const {t} = useTranslation()
  const [currentlyEditing, setCurrentlyEditing] = useState<
    | 'reorder'
    | 'text'
    | 'font'
    | 'color'
    | 'style'
    | 'size'
    | 'rotation'
    | 'curve'
    | 'distance1'
    | 'distance2'
    | 'move'
    | 'mirror'
    | undefined
  >('text')
  const dispatch = useAppDispatch()
  const {images, selectedImageId, printArea} = useAppSelector(
    store => store.editor,
  )
  const selectedImage = images.find(
    (image: Img) => image.id === selectedImageId,
  )
  const singleLine = isGraphics
  const closeControl = () => {
    setCurrentlyEditing(undefined)
  }

  useEffect(() => {
    setControlOpen(currentlyEditing !== undefined)
  }, [setControlOpen, currentlyEditing])

  useEffect(() => {
    if (!active) {
      closeControl()
    }
  }, [active])

  useEffect(() => {
    const selectedImg = images.find(
      (image: Img) => image.id === selectedImageId,
    )
    if (selectedImg?.type === 'caption') {
      setCurrentlyEditing('text')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImageId])

  return (
    <>
      <div className={`${cssMobile.tab} ${active ? cssMobile.active : ''}`}>
        <div
          className={cssMobile.action}
          onClick={() => {
            const newCaption: ImgCaption = {
              id: uuidv4(),
              type: 'caption',
              position: printArea?.position ?? origo,
              caption1: singleLine ? t('Szöveg helye') : t('Első sor'),
              caption2: singleLine ? null : t('Második sor'),
              width: 0,
              height: 0,
              fontFamily: 'Aclonica',
              color: '#000000',
              bold: false,
              italic: false,
              underline: false,
              scale: 6,
              rotation: 0,
              brightness: 1,
              mirroredX: false,
              mirroredY: false,
              inert: false,
              locked: false,
              curvatureLevel: 0,
              letterSpacing1: 0,
              letterSpacing2: 0,
              filters: {
                inverse: false,
                monochrome: false,
                sepia: false,
              },
            }
            dispatch(addImageSelected({image: newCaption}))
            dispatch(
              addImageHistoryItem({item: {img: newCaption, action: IMG_ADDED}}),
            )
            dispatch(selectToolboxTabPanel({tabPanel: 'Edit'}))
          }}>
          <FontAwesomeIcon icon={faPlus} />
          <span>{t('Hozzáadás')}</span>
        </div>
        <div className={cssMobile.scrollContainer}>
          <div
            className={cssMobile.action}
            onClick={() => {
              if (selectedImage) {
                dispatch(deleteImage({id: selectedImage.id}))
                dispatch(selectToolboxTabPanel({tabPanel: 'AddCaption'}))
                dispatch(
                  addImageHistoryItem({
                    item: {img: selectedImage, action: IMG_REMOVED},
                  }),
                )
              }
            }}>
            <FontAwesomeIcon icon={faTrash} />
            <span>Törlés</span>
          </div>
          <div
            className={cssMobile.action}
            onClick={() => {
              setCurrentlyEditing('text')
            }}>
            <FontAwesomeIcon icon={faAlignLeft} />
            <span>{t('Szöveg')}</span>
          </div>
          <div
            className={cssMobile.action}
            onClick={() => {
              setCurrentlyEditing('reorder')
            }}>
            <FontAwesomeIcon icon={faSortNumericUp} />
            <span>{t('Sorrend')}</span>
          </div>

          <div
            className={cssMobile.action}
            onClick={() => {
              setCurrentlyEditing('font')
            }}>
            <FontAwesomeIcon icon={faFont} />
            <span>{t('Betűtípus')}</span>
          </div>
          <div
            className={cssMobile.action}
            onClick={() => {
              setCurrentlyEditing('color')
            }}>
            <FontAwesomeIcon icon={faPaintBrush} />
            <span>{t('Szín')}</span>
          </div>
          <div
            className={cssMobile.action}
            onClick={() => {
              setCurrentlyEditing('style')
            }}>
            <FontAwesomeIcon icon={faMagic} />
            <span>{t('Stílus')}</span>
          </div>
          <div
            className={cssMobile.action}
            onClick={() => {
              setCurrentlyEditing('size')
            }}>
            <FontAwesomeIcon icon={faArrowsAltV} />
            <span>{t('Méret')}</span>
          </div>
          <div
            className={cssMobile.action}
            onClick={() => {
              setCurrentlyEditing('rotation')
            }}>
            <FontAwesomeIcon icon={faUndo} />
            <span>{t('Forgatás')}</span>
          </div>
          <div
            className={cssMobile.action}
            onClick={() => {
              setCurrentlyEditing('curve')
            }}>
            <FontAwesomeIcon icon={faShare} />
            <span>{t('Hajlítás')}</span>
          </div>
          <div
            className={cssMobile.action}
            onClick={() => {
              setCurrentlyEditing('distance1')
            }}>
            <FontAwesomeIcon icon={faTextWidth} />
            <span>{t('Betűtávolság')}</span>
          </div>
          <div
            className={cssMobile.action}
            onClick={() => {
              setCurrentlyEditing('distance2')
            }}>
            <FontAwesomeIcon icon={faTextWidth} />
            <span>{t('Betűtávolság 2')}</span>
          </div>
          <div
            className={cssMobile.action}
            onClick={() => {
              setCurrentlyEditing('move')
            }}>
            <FontAwesomeIcon icon={faArrowsAlt} />
            <span>{t('Mozgatás')}</span>
          </div>
          <div
            className={cssMobile.action}
            onClick={() => {
              setCurrentlyEditing('mirror')
            }}>
            <FontAwesomeIcon icon={faArrowsAltH} />
            <span>{t('Tükrözés')}</span>
          </div>
        </div>
      </div>
      <ReorderControls
        open={currentlyEditing === 'reorder'}
        onBackClick={closeControl}
      />
      <TextControls
        open={currentlyEditing === 'text'}
        onBackClick={closeControl}
      />
      <FontControls
        open={currentlyEditing === 'font'}
        onBackClick={closeControl}
      />
      <ColorControls
        open={currentlyEditing === 'color'}
        onBackClick={closeControl}
      />
      <StyleControls
        open={currentlyEditing === 'style'}
        onBackClick={closeControl}
      />
      <SizeControlsCaption
        open={currentlyEditing === 'size'}
        onBackClick={closeControl}
      />
      <RotationControls
        open={currentlyEditing === 'rotation'}
        onBackClick={closeControl}
      />
      <CurveControls
        open={currentlyEditing === 'curve'}
        onBackClick={closeControl}
      />
      <Distance1Controls
        open={currentlyEditing === 'distance1'}
        onBackClick={closeControl}
      />
      <Distance2Controls
        open={currentlyEditing === 'distance2'}
        onBackClick={closeControl}
      />
      <MoveControls
        open={currentlyEditing === 'move'}
        onBackClick={closeControl}
      />
      <MirrorControls
        open={currentlyEditing === 'mirror'}
        onBackClick={closeControl}
      />
    </>
  )
}
